import {
  Company,
  Branch,
  CompanyList,
  CompanyExtraDetails,
  ResultBranch,
  ResultTerminal,
  ActivationQRData,
  TerminalDataRequest,
  TerminalDataResponse,
  TerminalRenameRequest,
  TerminalRenameResponse,
  Device,
  Terminal,
  ResultCompanySmall,
  ResultCompanyFull,
  jDateTimeToString
} from "../types";
import * as util from "@/util";
import { postServiceCall } from "./base";
import store from "../store";
import { sortBy } from "lodash";

export async function getMyCompanies(): Promise<Company[]> {
  const result = await postServiceCall<CompanyList>("companyList", { language: store.state.lang.key });
  return result.companies.map((cpy) => ({
    id: cpy.id,
    name: cpy.name,
    address: addresses(cpy),
    city: cpy.city,
    province: cpy.province,
    postCode: cpy.postCode,
    branchesSummaries: cpy.branches ?? [],
    branches: undefined
  }));
}

export async function getCompanyData(companyID: number): Promise<Company> {
  const cpy = await postServiceCall<ResultCompanyFull>("companyData", { companyID, language: store.state.lang.key });
  return mapCompanyFull(cpy);
}

export async function getTerminalData(
  companyID: number,
  branchID: number,
  terminalID: number,
  tcod: string,
  date: string
): Promise<TerminalDataResponse> {
  const req: TerminalDataRequest = {
    companyID,
    branchID,
    terminalID,
    tcod,
    date,
    language: store.state.lang.key
  };
  return await postServiceCall("terminalData", req);
}

export async function getTerminalQR(qrData: ActivationQRData): Promise<ActivationQRData> {
  return await postServiceCall("terminalQR", { ...qrData, language: store.state.lang.key });
}

export async function renameTerminal(rq: TerminalRenameRequest): Promise<TerminalRenameResponse> {
  return await postServiceCall("terminalRename", rq);
}

export async function getCompanyDetails(companyID: number): Promise<CompanyExtraDetails> {
  return await postServiceCall("companyDetailsGet", { companyID, language: store.state.lang.key });
}

export async function saveCompanyDetails(details: CompanyExtraDetails): Promise<{ success: boolean }> {
  return await postServiceCall("companyDetailsSave", details);
}

function addresses(cpy: ResultCompanySmall | ResultCompanyFull | ResultBranch): string[] {
  const addresses = [cpy.addressLine1 || "", cpy.addressLine2 || ""];
  let lastAddress = cpy.postCode ?? "";
  if (cpy.city) lastAddress += ", " + cpy.city;
  if (cpy.province) lastAddress += ", " + cpy.province;
  addresses.push(lastAddress);
  return addresses.filter((addr) => !!addr);
}

function mapCompanyFull(cpy: ResultCompanyFull): Company {
  const branches = cpy.branches ? cpy.branches.map((branch) => mapBranch(branch)) : undefined;
  return {
    id: cpy.id,
    name: cpy.name,
    address: addresses(cpy),
    city: cpy.city,
    province: cpy.province,
    postCode: cpy.postCode,
    branchesSummaries: branches ?? [],
    branches: branches
  };
}

function mapBranch(branch: ResultBranch): Branch {
  let name = branch.name;
  if (!!branch.customerID && branch.customerID !== "") {
    name = branch.customerID + " - " + name;
  }
  return {
    id: branch.id,
    name,
    customerID: branch.customerID,
    address: addresses(branch),
    city: branch.city,
    province: branch.province,
    postCode: branch.postCode,
    paymentLink: branch.paymentLink,
    isVending: branch.isVending,
    manualClose: branch.manualClose ?? false,
    terminals: branch.terminals ? branch.terminals.map((term) => mapTerminal(term, branch.devices)) : undefined,
    devices: branch.devices ? branch.devices : undefined,
    commerceCodes: branch.commerceCodes
  };
}

function mapTerminal(term: ResultTerminal, devices?: Device[]): Terminal {
  const device = findMatchingDevice(term, devices);
  return {
    ...term,
    device,
    serialNumber: device?.serialNumber ?? null
  };
}

function findMatchingDevice(term: ResultTerminal, devices?: Device[] | null): Device | null {
  const connectedDevices = devices?.filter((d) => d.connectedToTCOD === term.tcod) ?? null;
  if (!connectedDevices || connectedDevices.length == 0) {
    return null;
  }
  if (connectedDevices.length == 1) {
    return connectedDevices[0];
  }
  // Si hay varios dispositivos buscamos el que coincide el número de serie
  const device =
    connectedDevices.find((d) => !!d.serialNumber && term.machineAndLocalIP?.includes(d.serialNumber.toString())) ??
    null;
  if (device) return device;
  // Si ningún dispositivo coincide se busca el que tenga la fecha de conexión más reciente
  const sortedDevices = sortBy(connectedDevices, [
    function (o) {
      return jDateTimeToString(o.connectedTimestamp) ?? "0";
    }
  ]);
  return sortedDevices[sortedDevices.length - 1];
}

export async function importData(collection: string, documents: unknown[], refreshCompany?: boolean): Promise<unknown> {
  const result = await postServiceCall("import", {
    collection,
    documents,
    refreshCompany,
    language: store.state.lang.key
  });
  util.log("SVC Imported", result);
  return result;
}
